import { Injectable } from "@angular/core";
import { IActionGroup } from "app/_models/IActionGroup";
import { AppComponent } from "app/app.component";
import { RADService } from "app/_services/rad.service";
import { IParameter } from "app/_models/IParameter";
import { ILookupValues } from "app/_models/ILookupValues";
import { ISelectedLine } from "app/_models/ISelectedLine";
import { IReportGroup } from "app/_models/IReportGroup";
import { IDashboardGroup } from "app/_models/IDashboardGroup";
import { ISourceViewHeader } from "app/_models/ISourceViewHeader";
import { IGanttTask } from "app/_models/IGanttTask";

@Injectable({ providedIn: 'root' })
export class RADController {
  j: number = 0;
  constructor(
    private app: AppComponent,
    private radService: RADService
  ) { }

  getActions(nodeId: string, userId: string): Promise<IActionGroup[]> {
    return new Promise<IActionGroup[]>((resolve) => {
      let formData = this.app.buildForm(['nodeId', 'userId'], [nodeId, userId])
      this.radService.getActions(formData).subscribe({
        next: (data: IActionGroup[]) => { resolve(data ? data : []); },
        error: (errorLog) => { console.log(errorLog); }
      });
    });
  }

  getReports(nodeId: string, userId: string): Promise<IReportGroup[]> {
    return new Promise<IReportGroup[]>((resolve) => {
      let formData = this.app.buildForm(['nodeId', 'userId'], [nodeId, userId]);
      this.radService.getReports(formData).subscribe({
        next: (data: IReportGroup[]) => { resolve(data ? data : []); },
        error: (errorLog) => { console.log(errorLog); }
      });
    });
  }

  getDashboards(nodeId: string, userId: string): Promise<IDashboardGroup[]> {
    return new Promise<IDashboardGroup[]>((resolve) => {
      let formData = this.app.buildForm(['nodeId', 'userId'], [nodeId, userId]);
      this.radService.getDashboards(formData).subscribe({
        next: (data: IDashboardGroup[]) => { resolve(data ? data : []); },
        error: (errorLog) => { console.log(errorLog); }
      });
    });
  }

  getLookupData(sql: string, filterValue: string, id: string, userId: string): Promise<ILookupValues[]> {
    return new Promise<ILookupValues[]>((resolve) => {
      let formData = this.app.buildForm(['sql', 'filterValue', 'id', 'userId'], [sql, filterValue, id, userId]);
      this.radService.getLookupData(formData).subscribe({
        next: (data: ILookupValues[]) => { resolve(data ? data : []); },
        error: (errorLog) => { console.log(errorLog); }
      });
    });
  }

  getSourceViewHeaders(nodeId: string, userId: string, sourceView: string): Promise<ISourceViewHeader[]> {
    return new Promise<ISourceViewHeader[]>((resolve) => {
      let formData = this.app.buildForm(['nodeId', 'userId', 'sourceView'], [nodeId, userId, sourceView]);
      this.radService.getSourceViewHeaders(formData).subscribe({
        next: (data: ISourceViewHeader[]) => { resolve(data ? data : []); },
        error: (errorLog) => { console.log(errorLog); }
      });
    });
  }

  sqlExecute(nodeId: string, userId: string, actionId: string, id: string, executePerLine: boolean): Promise<string> {
    return new Promise<string>((resolve) => {      
      let keys = ['nodeId', 'userId', 'actionId', 'id', 'executePerLine'];
      let values = [nodeId, userId, actionId, id, executePerLine.toString()];
      let formData = this.app.buildForm(keys, values);
      this.radService.sqlExecute(formData).subscribe({
        next: (data: { result: string }) => { resolve(data.result); },
        error: (errorLog) => { console.log(errorLog); }
      });
    });
  }  

  async createUpdateSingleRecord(nodeId: string, userId: string, actionId: string, id: string, parameters?: IParameter[]): Promise<string> {
    return new Promise<string>((resolve) => {
      let idChecks;

      if (sessionStorage.getItem('qx_action_idcheck')) {
        idChecks = JSON.parse(sessionStorage.getItem('qx_action_idcheck'));        
      }
      
      let keys = ['nodeId', 'userId', 'actionId', 'id', 'parameters', 'requiresId', 'requiresUserId'];
      let values = [nodeId, userId, actionId, id, JSON.stringify(parameters), idChecks.requiresId, idChecks.requiresUserId];
      let formData = this.app.buildForm(keys, values);      
      this.radService.createUpdateSingleRecord(formData).subscribe({
        next: (data: { result: string }) => { resolve(data.result); },
        error: (errorLog) => { console.log(errorLog); }
      });
    });
  }

  filterRadButtons<T extends (IActionGroup[] | IReportGroup[] | IDashboardGroup[]), U extends ISelectedLine[] | IGanttTask[]>(group: T, currentViewName: string, selectedItems: U): T {
    group.forEach(group => {
      group.value.forEach(button => {
        if (button.activeView != null || button.activeViews != null) {
          if (!button.executePerLine) button.disabled = false;
          else if ((button.activeView && button.activeView.includes(currentViewName)) || (button.activeViews && button.activeViews.includes(currentViewName))) {
            if (button.executePerLine && selectedItems.length == 0) button.disabled = true;
            else button.disabled = false;
          } else button.disabled = true;
        } else button.disabled = false;
      })
    })

    return group;
  }

  executeSqlStatement(sql: string, userId: string): Promise<any> {
    return new Promise<string>((resolve) => {
      let formData = this.app.buildForm(['sql', 'userId'], [sql, userId]);
      this.radService.executeSqlStatement(formData).subscribe({
        next: (data: any) => { resolve(data); },
        error: (errorLog) => { console.log(errorLog); }
      });
    });
  }
  
  async loadSourceViewHeaders(parameters: IParameter[], nodeId: string, userId: string): Promise<IParameter[]> {
    if (this.j === parameters.length) {
      this.j = 0;
      return parameters;
    }

    let param = parameters[this.j];
    param.sourceViewHeaders = [];
    if (this.j < parameters.length && param.sourceView) {
      let result = await this.getSourceViewHeaders(nodeId, userId, param.sourceView);
      param.sourceViewHeaders = result;
    }

    this.j++;
    return this.loadSourceViewHeaders(parameters, nodeId, userId);
  }  

  checkIdRequirements(nodeId: string, actionId: string): Promise<any> {
    return new Promise<any>((resolve) => {
      let formData = this.app.buildForm(['nodeId', 'actionId'], [nodeId, actionId]);
      this.radService.checkIdRequirements(formData).subscribe({
        next: (data: any) => { resolve(data); },
        error: (errorLog) => { console.log(errorLog); }
      });
    });    
  }  

  getDashboardCarouselData(carouselId: string): Promise<any> {
    return new Promise<any>((resolve) => {      
      let formData = this.app.buildForm(['carouselId'], [carouselId]);
      this.radService.getDashboardCarouselData(formData).subscribe({
        next: (data: any) => { resolve(data.result ? data.result : []); },
        error: (errorLog) => { console.log(errorLog); }
      });
    });
  }
}